import { memo } from "react";

import { cn } from "@/lib/chillnn/cn";

import { externalLink } from "@/routes/_constants/external-link";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion/accordion";

import { DAILY_RATE_CELL_STATUS_LABEL } from "../../model/types";

/**
 * 日別料金一覧のサポート記事パス。公開後に Notion ページ ID 付きパスを入れてリンクを出す。
 * 例: `/faq/host/reservation/<pageId>`
 */
const RATE_MATRIX_GUIDE_PATH: string | null = null;

const toRateMatrixGuideUrl = (path: string | null): string | null => {
	if (path === null) return null;
	return `${externalLink.faqSite}${path}`;
};

const RATE_MATRIX_GUIDE_URL = toRateMatrixGuideUrl(RATE_MATRIX_GUIDE_PATH);

const cellSamples = [
	{
		key: "unregistered",
		sample: DAILY_RATE_CELL_STATUS_LABEL.unregistered,
		sampleClassName: "bg-white text-not-entered",
		title: "未設定",
		description: "料金が設定されてません",
	},
	{
		key: "stop_sale",
		sample: DAILY_RATE_CELL_STATUS_LABEL.stop_sale,
		sampleClassName: "bg-white text-not-entered",
		title: "売止",
		description: "この日は販売しません",
	},
	{
		key: "not_applicable",
		sample: DAILY_RATE_CELL_STATUS_LABEL.not_applicable,
		sampleClassName: "bg-zinc-100 text-caption-50",
		title: "対象外",
		description: "この人数では予約できません",
	},
	{
		key: "price",
		sample: "12,000",
		sampleClassName: "bg-white text-zinc-950",
		title: "数字",
		description: "1泊1部屋の税込料金です",
	},
] as const;

export const PlanRateMatrixLegend = memo(() => {
	return (
		<div>
			<h5 className="font-semibold">表の見方</h5>
			<p className="mx-4 mt-1 text-xs leading-relaxed text-caption-50">
				数字は1泊1部屋の税込です。特別な表示がなければ、部屋料金とプラン料金の合計です。
			</p>
			{RATE_MATRIX_GUIDE_URL == null ? null : (
				<p className="mx-4 mt-1 text-xs text-caption-50">
					くわしい説明は
					<a
						href={RATE_MATRIX_GUIDE_URL}
						target="_blank"
						rel="noopener noreferrer"
						className="text-link-400 underline-offset-4 hover:underline"
					>
						サポートページ
					</a>
					を見てください。
				</p>
			)}
			<div className="mx-4">
				<Accordion type="multiple">
					<AccordionItem value="cells">
						<AccordionTrigger>
							<p className="text-sm">マスに出る文字</p>
						</AccordionTrigger>
						<AccordionContent>
							<ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
								{cellSamples.map((item) => (
									<li
										key={item.key}
										className="flex items-center gap-x-3 rounded-md border border-zinc-100 bg-white px-3 py-2"
									>
										<span
											className={cn(
												"inline-flex h-10 w-20 shrink-0 items-center justify-center border border-zinc-200 text-sm tabular-nums",
												item.sampleClassName,
											)}
										>
											{item.sample}
										</span>
										<span className="min-w-0">
											<span className="block text-sm font-medium text-zinc-800">{item.title}</span>
											<span className="block text-xs leading-relaxed text-caption-50">
												{item.description}
											</span>
										</span>
									</li>
								))}
							</ul>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			</div>
		</div>
	);
});

PlanRateMatrixLegend.displayName = "PlanRateMatrixLegend";
